import { Alert, Badge, Button, Card, Checkbox, Code, Group, ScrollArea, Stack, Table, Text, Title } from "@mantine/core";
import { useState } from "react";
import { trpcSlice } from "../../../trpcSlice";

interface IMaconomyUserStatusSyncChange {
  field: string;
  from?: string | null;
  to?: string | null;
}

interface IMaconomyUserStatusSyncItem {
  employeeNumber: string;
  employeeName?: string | null;
  dateEndEmployment?: string | null;
  changes: IMaconomyUserStatusSyncChange[];
}

interface IMaconomyUserStatusSyncResult {
  dryRun: boolean;
  scannedEmployees: number;
  matchedUsers: number;
  updatedUsers: number;
  unchangedUsers: number;
  employeeNumbersWithMultipleRows: string[];
  updated: IMaconomyUserStatusSyncItem[];
  unchanged: IMaconomyUserStatusSyncItem[];
}

const appAdminMaconomyUserStatusSyncApi = trpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    syncUsersFromMaconomyEmployees: builder.mutation<
      IMaconomyUserStatusSyncResult,
      { dryRun?: boolean; includeUnchanged?: boolean }
    >({
      query: (body) => ({
        url: "/appadmin/maconomy/users/sync",
        method: "POST",
        body: {
          dryRun: body.dryRun ?? true,
          includeUnchanged: body.includeUnchanged ?? false,
        },
      }),
    }),
  }),
});

const { useSyncUsersFromMaconomyEmployeesMutation } = appAdminMaconomyUserStatusSyncApi;

const getMaconomyStatusChange = (item: IMaconomyUserStatusSyncItem) =>
  item.changes.find((change) => change.field === "maconomyStatus");

const formatDate = (dateValue?: string | null) => {
  if (dateValue == null || dateValue === "") {
    return "";
  }

  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? dateValue : date.toLocaleDateString("nb-NO");
};

export const MaconomyUserStatusSyncAdmin = () => {
  const [dryRun, setDryRun] = useState(true);
  const [includeUnchanged, setIncludeUnchanged] = useState(false);
  const [syncUsers, { data, error, isLoading }] = useSyncUsersFromMaconomyEmployeesMutation();

  const runSync = () => {
    syncUsers({ dryRun, includeUnchanged });
  };

  const errorMessage =
    (error as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (error as { message?: string } | undefined)?.message;

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={3}>Synk brukerstatus fra Maconomy</Title>
            <Text size="sm" c="dimmed">
              Leser mc_employees, matcher users på employeeNumber og oppdaterer bare maconomyStatus.
            </Text>
          </Stack>
          {isLoading && <Badge variant="light">Kjører</Badge>}
        </Group>

        <Group align="center" gap="md">
          <Checkbox
            label="Dry-run"
            checked={dryRun}
            onChange={(event) => setDryRun(event.currentTarget.checked)}
          />
          <Checkbox
            label="Ta med uendrede i resultatet"
            checked={includeUnchanged}
            onChange={(event) => setIncludeUnchanged(event.currentTarget.checked)}
          />
          <Button loading={isLoading} onClick={runSync}>
            {dryRun ? "Kjør dry-run" : "Oppdater maconomyStatus"}
          </Button>
        </Group>

        {errorMessage != null && (
          <Alert color="red" variant="light">
            {errorMessage}
          </Alert>
        )}

        {data != null && (
          <Stack gap="sm">
            <Group gap="xs">
              <Badge color={data.dryRun ? "blue" : "green"} variant="light">
                {data.dryRun ? "Dry-run" : "Skrevet"}
              </Badge>
              <Badge variant="light">{data.scannedEmployees} ansatte lest</Badge>
              <Badge variant="light">{data.matchedUsers} users matchet</Badge>
              <Badge color={data.updatedUsers > 0 ? "yellow" : "gray"} variant="light">
                {data.updatedUsers} endres
              </Badge>
              <Badge variant="light">{data.unchangedUsers} uendret</Badge>
              {data.employeeNumbersWithMultipleRows.length > 0 && (
                <Badge color="orange" variant="light">
                  {data.employeeNumbersWithMultipleRows.length} duplikate employeeNumber
                </Badge>
              )}
            </Group>

            {data.employeeNumbersWithMultipleRows.length > 0 && (
              <Alert color="yellow" variant="light">
                <Text size="sm" fw={600}>
                  Flere rå-rader finnes for noen employeeNumber. Nyeste rad brukes.
                </Text>
                <Code>{data.employeeNumbersWithMultipleRows.join(", ")}</Code>
              </Alert>
            )}

            <Stack gap="xs">
              <Text fw={600}>Endringer {data.updated.length < data.updatedUsers ? "(sample)" : ""}</Text>
              {data.updated.length > 0 ? (
                <ScrollArea h={320} type="auto">
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Ansattnr</Table.Th>
                        <Table.Th>Navn</Table.Th>
                        <Table.Th>Sluttdato</Table.Th>
                        <Table.Th>Fra</Table.Th>
                        <Table.Th>Til</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.updated.map((item) => {
                        const maconomyStatusChange = getMaconomyStatusChange(item);
                        return (
                          <Table.Tr key={item.employeeNumber}>
                            <Table.Td>{item.employeeNumber}</Table.Td>
                            <Table.Td>{item.employeeName ?? ""}</Table.Td>
                            <Table.Td>{formatDate(item.dateEndEmployment)}</Table.Td>
                            <Table.Td>{maconomyStatusChange?.from ?? ""}</Table.Td>
                            <Table.Td>{maconomyStatusChange?.to ?? ""}</Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              ) : (
                <Text size="sm" c="dimmed">
                  Ingen endringer.
                </Text>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
